import { RadioTower, Trash2, Plus, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { RestreamPlatform } from '@masters-club/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type ExternalRestreamTarget,
  RESTREAM_PLATFORM_LABELS,
  RESTREAM_PLATFORM_URL_HINTS,
} from '../types';

/**
 * Редактор списка внешних площадок рестрима. Управляемый компонент:
 * родитель хранит массив targets в state и передаёт value/onChange.
 *
 * id генерируется на бэке при сохранении (схема делает id опциональным на
 * входе), поэтому здесь для новых строк используется временный clientId,
 * который не отправляется (бэк проставит стабильный). Чтобы React-ключи были
 * стабильны при редактировании, держим временный ключ в локальном Map по
 * индексу — проще: ключ = id (если есть) либо индекс.
 */

const PLATFORM_OPTIONS = Object.values(RestreamPlatform);

interface RestreamTargetsFieldProps {
  value: ExternalRestreamTarget[];
  onChange: (next: ExternalRestreamTarget[]) => void;
}

function emptyTarget(): ExternalRestreamTarget {
  return {
    // временный id для React-ключа и адресации статусов до первого сохранения;
    // бэк перезапишет своим стабильным значением.
    id: `new-${Math.random().toString(36).slice(2, 10)}`,
    platform: RestreamPlatform.YOUTUBE,
    rtmpUrl: '',
    streamKey: '',
    enabled: true,
  };
}

export function RestreamTargetsField({ value, onChange }: RestreamTargetsFieldProps) {
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  const update = (index: number, patch: Partial<ExternalRestreamTarget>) => {
    onChange(value.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };

  const remove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...value, emptyTarget()]);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Поток уходит на каждую включённую площадку без перекодирования
        (ffmpeg&nbsp;-c&nbsp;copy). Stream key — секрет: вводите его из кабинета
        площадки. Для приватных трансляций помните, что контент уйдёт в открытый
        доступ.
      </p>

      {value.length === 0 && (
        <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
          Площадки рестрима не добавлены
        </div>
      )}

      {value.map((target, index) => {
        const showKey = revealed[target.id] ?? false;
        return (
          <div
            key={target.id}
            className="space-y-3 rounded-md border p-3"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <RadioTower className="h-4 w-4 text-muted-foreground" />
                <div className="w-48">
                  <Select
                    value={target.platform}
                    onValueChange={(v) =>
                      update(index, {
                        platform: v as ExternalRestreamTarget['platform'],
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLATFORM_OPTIONS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {RESTREAM_PLATFORM_LABELS[p]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={target.enabled}
                    onCheckedChange={(checked) => update(index, { enabled: checked })}
                  />
                  <span className="text-xs text-muted-foreground">
                    {target.enabled ? 'Активна' : 'Выключена'}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(index)}
                  aria-label="Удалить площадку"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">RTMP(S) URL</Label>
                <Input
                  value={target.rtmpUrl}
                  placeholder={RESTREAM_PLATFORM_URL_HINTS[target.platform]}
                  onChange={(e) => update(index, { rtmpUrl: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Stream key</Label>
                <div className="flex items-center gap-1">
                  <Input
                    type={showKey ? 'text' : 'password'}
                    value={target.streamKey}
                    placeholder="секрет из кабинета площадки"
                    onChange={(e) => update(index, { streamKey: e.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setRevealed((prev) => ({ ...prev, [target.id]: !showKey }))
                    }
                    aria-label={showKey ? 'Скрыть ключ' : 'Показать ключ'}
                  >
                    {showKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="mr-2 h-4 w-4" />
        Добавить площадку
      </Button>
    </div>
  );
}
